import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { cn } from 'src/shared/components/ui/utils'

const ACCENT = {
  orange: {
    panel: 'from-orange-50/90 via-[hsl(var(--surface-panel)/0.7)] to-transparent',
    icon: 'bg-orange-100 text-orange-700',
    count: 'bg-gradient-to-br from-orange-400 to-orange-600 text-white',
  },
  violet: {
    panel: 'from-violet-50/90 via-[hsl(var(--surface-panel)/0.7)] to-transparent',
    icon: 'bg-violet-100 text-violet-700',
    count: 'bg-gradient-to-br from-violet-400 to-violet-600 text-white',
  },
  blue: {
    panel: 'from-sky-50/90 via-[hsl(var(--surface-panel)/0.7)] to-transparent',
    icon: 'bg-sky-100 text-sky-700',
    count: 'bg-gradient-to-br from-sky-500 to-blue-600 text-white',
  },
}

export const DashboardSection = ({
  title,
  description,
  count,
  icon: Icon,
  accent = 'orange',
  viewAllHref,
  viewAllLabel,
  children,
}: {
  title: string
  description: string
  count: number
  icon: LucideIcon
  accent?: keyof typeof ACCENT
  viewAllHref?: string
  viewAllLabel?: string
  children: ReactNode
}) => {
  const navigate = useNavigate()
  const style = ACCENT[accent]

  return (
    <Column
      className={cn(
        'gap-4 rounded-[var(--radius-outer)] border border-[hsl(var(--border-subtle))] p-5',
        'bg-gradient-to-br shadow-soft',
        style.panel
      )}
    >
      <Row className="items-start justify-between gap-4">
        <Row className="min-w-0 items-start gap-3">
          <div
            className={cn(
              'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              style.icon
            )}
          >
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
          <Column className="min-w-0 gap-1">
            <Row className="items-center gap-2.5">
              <h2>{title}</h2>
              {count > 0 ? (
                <span
                  className={cn(
                    'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-semibold tabular-nums shadow-chip',
                    style.count
                  )}
                >
                  {count}
                </span>
              ) : null}
            </Row>
            <p className="text-sm text-muted-foreground">{description}</p>
          </Column>
        </Row>

        {viewAllHref && viewAllLabel && count > 0 ? (
          <Button
            variant="glass"
            size="sm"
            radius="lg"
            className="shrink-0"
            onClick={() => navigate(viewAllHref)}
          >
            {viewAllLabel}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        ) : null}
      </Row>

      {children}
    </Column>
  )
}
